/**
 * ISSUE-964: keeps the fan's unconfirmed pinned league list so a pin, unpin
 * or reorder shows at once instead of waiting on the server. It sends at
 * most one `/follows/set` request at a time, and after each confirmed send
 * either stops or sends the newest list, so rapid clicks are never lost.
 * It never reads the query cache while a list is pending, because the
 * generated `useFollowsSet` refetches the profile 150ms after every
 * success, and a refetch landing mid-burst would overwrite the newest click.
 */

export type typeLeaguePinBody = {
  followedSportIds: string[];
  followedLeagueIds: string[];
};

export type typeLeaguePinReply = {
  error?: { code: string; message: string } | null;
  data?: null | { followedSportIds: string[]; followedLeagueIds: string[] };
};

export type typeLeaguePinSend = (
  body: typeLeaguePinBody,
) => Promise<typeLeaguePinReply>;

export type typeLeaguePinSaverDeps = {
  /** Called after every successful send, with the list the server confirmed.
   *  The saver awaits it before deciding whether to send again. */
  onConfirmed: (followedLeagueIds: string[]) => Promise<void>;
  /** Called once when a send fails. `reply` is the error envelope, or null
   *  when the send threw. */
  onFailed: (reply: typeLeaguePinReply | null) => void;
};

export type typeLeaguePinSaver = {
  /** The unconfirmed list, or null when nothing is waiting on the server.
   *  Returns the SAME array reference until the next change, as
   *  useSyncExternalStore requires. */
  getPendingLeagueIds: () => string[] | null;
  subscribe: (listener: () => void) => () => void;
  apply: (input: {
    serverLeagueIds: string[];
    followedSportIds: string[];
    change: (current: string[]) => string[];
    send: typeLeaguePinSend;
  }) => void;
};

export function createLeaguePinSaver(
  deps: typeLeaguePinSaverDeps,
): typeLeaguePinSaver {
  let pending: string[] | null = null;
  let followedSportIds: string[] = [];
  let latestSend: typeLeaguePinSend | null = null;
  let isSending = false;
  const listeners = new Set<() => void>();

  function notify(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function sameList(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }

  async function drain(): Promise<void> {
    isSending = true;
    for (;;) {
      const sent = pending;
      const send = latestSend;
      if (sent === null || send === null) {
        isSending = false;
        return;
      }
      let reply: typeLeaguePinReply | null;
      try {
        reply = await send({
          followedSportIds,
          followedLeagueIds: sent,
        });
      } catch {
        reply = null;
      }
      if (reply === null || reply.error) {
        pending = null;
        isSending = false;
        notify();
        deps.onFailed(reply);
        return;
      }
      const confirmed = reply.data?.followedLeagueIds ?? sent;
      await deps.onConfirmed(confirmed);
      if (pending === sent) {
        pending = null;
        isSending = false;
        notify();
        return;
      }
    }
  }

  return {
    getPendingLeagueIds: () => pending,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    apply: (input) => {
      const base = pending ?? input.serverLeagueIds;
      const next = input.change(base);
      if (sameList(next, base)) {
        return;
      }
      pending = next;
      followedSportIds = input.followedSportIds;
      latestSend = input.send;
      notify();
      if (!isSending) {
        void drain();
      }
    },
  };
}
